import React, { useRef, useEffect, useState } from 'react';

// Components
import LineItem from './line-item';
import EmptyCart from './empty-cart';

// Utility functions
import { getDisplayPrice } from '~js/utils/utils';
import { updateCartCount } from '~js/utils/cart-drawer-utils';
import { renderIcon } from '~js/utils/react-utils';
import { disableBodyScroll, enableBodyScroll } from '~js/utils/dom';
import { fetchVariantMetafields, cartApi } from '~js/utils/cart-api';

const schemaConfigScript = document.getElementById('cart-drawer-config');
const cartConfigData = schemaConfigScript
  ? JSON.parse(schemaConfigScript.textContent)
  : {};
const cartConfig = {
  ...cartConfigData.section,
};
window.cartConfig = cartConfig;

const CartDrawer = () => {
  const [cartDrawerLoaded, setCartDrawerLoaded] = useState(false);
  const [cartState, setCartState] = useState({
    itemCount: 0,
    subtotal: 0,
    subtotalVal: 0,
    lineItems: [],
  });
  const [loading, setLoading] = useState(null);
  const [hasLimitExceeded, setHasLimitExceeded] = useState(false);
  const [cartFullyLoaded, setCartFullyLoaded] = useState(false);

  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.addEventListener('close', runOnDialogClose);

    const initializeCartDrawer = async () => {
      await fetchCart();
      window.CartDrawer = {
        show,
        hide,
        fetch: fetchCart,
      };
      if (window.location.search.includes('viewcart=true')) {
        show(false);
      }
    };

    initializeCartDrawer();

    return () => {
      dialog?.removeEventListener('close', runOnDialogClose);
      delete window.CartDrawer;
    };
  }, []);

  useEffect(() => {
    const hasTankExchange = cartState.lineItems.some((item) => {
      return (
        item.variant?.title?.toLowerCase().includes('tank exchange') ||
        item.title?.toLowerCase().includes('tank exchange')
      );
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        note: hasTankExchange ? 'tank exchange' : '',
      }),
    });
  }, [cartState]);

  const fetchCart = async () => {
    setCartFullyLoaded(false);
    try {
      const response = await fetch('/cart?view=jsondata');
      const data = await response.json();

      let limitExceeded = false;

      if (data.cart.items.length === 0) {
        setCartState({
          itemCount: 0,
          subtotal: 0,
          subtotalVal: 0,
          lineItems: [],
        });
        setHasLimitExceeded(false);
        return;
      }

      const variantIds = data.cart.items.map((item) => item.variant_id);
      const metafieldsData = await fetchVariantMetafields(variantIds);

      const metafieldMap = {};
      metafieldsData.forEach((mf) => {
        const variantId = mf.id.split('/').pop();
        metafieldMap[variantId] = {
          maxLimit: mf.maxLimit,
          limitMessage: mf.limitMessage,
        };
      });

      const itemsNeedingAdjustment = [];

      const lineItemsWithLimits = data.cart.items.map((item) => {
        const variantId = String(item.variant_id);
        const { maxLimit = 0, limitMessage = '' } =
          metafieldMap[variantId] || {};

        const exceedsLimit = maxLimit > 0 && item.quantity > maxLimit;
        if (exceedsLimit) {
          limitExceeded = true;
          itemsNeedingAdjustment.push({
            key: item.key,
            maxLimit,
          });
        }

        return {
          ...item,
          maxLimit,
          limitMessage,
          exceedsLimit,
        };
      });

      // If any items exceed the max limit, fix them automatically
      if (itemsNeedingAdjustment.length > 0) {
        for (const item of itemsNeedingAdjustment) {
          cartApi.updateQty(item.key, item.maxLimit, 'updateQty', setLoading);
        }
        // Re-fetch cart after updates
        setTimeout(fetchCart, 0);
        return;
      }

      setHasLimitExceeded(limitExceeded);
      setCartState({
        itemCount: data.cart.item_count,
        subtotal: data.cart.items_subtotal_price,
        subtotalVal: data.cart.items_subtotal_price / 100,
        lineItems: lineItemsWithLimits,
      });

      updateCartCount(data.cart.item_count);
      window.cartData = {
        ...(window.cartData || {}),
        itemCount: data.cart.item_count,
      };

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { itemCount: data.cart.item_count },
        }),
      );
    } catch (error) {
      console.error('Error fetching Shopify cart:', error);
    } finally {
      setCartFullyLoaded(true);
    }
  };

  const show = (fetch = true) => {
    dialogRef.current?.showModal();
    document.body.classList.add('scroll-lock');
    disableBodyScroll();

    if (fetch) fetchCart();

    setTimeout(() => {
      setCartDrawerLoaded(true);
    }, 10);
  };

  const hide = () => {
    setCartDrawerLoaded(false);
    setTimeout(() => {
      dialogRef.current?.close();
    }, 300);
  };

  const runOnDialogClose = () => {
    document.body.classList.remove('scroll-lock');
    enableBodyScroll();
    setCartDrawerLoaded(false);
  };

  const dialogEscapeHandler = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      hide();
    }
  };

  const handleOuterClick = (e) => {
    if (e.target === e.currentTarget) {
      hide();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="cart-drawer-wrapper backdrop:bg-gray-950/60"
      aria-labelledby="cartDrawerTitle"
      onKeyDown={dialogEscapeHandler}
      onMouseDown={handleOuterClick}
    >
      <div
        className={`fixed right-0 top-0 z-40 flex h-full w-[358px] justify-end duration-300 lg:w-[480px] ${
          !cartDrawerLoaded ? 'opacity-0' : ''
        }`}
      >
        <div
          className={`cart-drawer-content max-w-screen-xxs relative flex h-full max-h-screen w-full flex-col bg-white opacity-100 transition-transform duration-300 lg:max-w-[480px] ${
            !cartDrawerLoaded ? 'translate-x-full' : ''
          }`}
        >
          <div className="cart-drawer-header flex items-center justify-between gap-5 border-b border-gray-100 p-4">
            <h2
              id="cartDrawerTitle"
              className="flex items-center gap-1 text-base text-xl font-medium text-blue-950"
            >
              {cartConfig.header_title}
              <span className="flex-shrink-0 text-sm font-book">
                ({cartState.itemCount}{' '}
                {cartState.itemCount === 1 ? 'item' : 'items'})
              </span>
            </h2>
            <button onClick={hide} aria-label="Close Cart Drawer">
              {renderIcon('close')}
            </button>
          </div>

          <div className="cart-drawer-body relative z-[1] flex-grow overflow-y-auto px-3 pt-4">
            {cartState.lineItems.length > 0 ? (
              <div className="flex flex-col gap-6">
                {cartState.lineItems.map((item) => (
                  <LineItem
                    key={item.key}
                    item={item}
                    cartConfig={cartConfig}
                    loading={loading}
                    setLoading={setLoading}
                    removeText={cartConfig.remove_product_text}
                  />
                ))}
              </div>
            ) : (
              <EmptyCart cartConfig={cartConfig} />
            )}
          </div>

          {cartState.lineItems.length > 0 && (
            <div className="cart-drawer-footer flex flex-col gap-3 border-t border-gray-100 p-4">
              <div className="flex items-center justify-between text-blue-950">
                <span className="font-book">
                  {cartConfig.subtotal_text} ({cartState.itemCount}{' '}
                  {cartState.itemCount === 1 ? 'item' : 'items'})
                </span>
                <span className="font-medium">
                  {getDisplayPrice(cartState.subtotal)}
                </span>
              </div>
              <div>
                <a
                  className={`flex h-14 w-full items-center justify-center rounded-md px-4 py-3.5 font-semibold text-gray-10 transition
                  ${
                    !cartFullyLoaded || hasLimitExceeded
                      ? 'cursor-not-allowed bg-gray-200'
                      : 'bg-blue-400 hover:bg-blue-950'
                  }`}
                  href="/checkout"
                  onClick={(e) =>
                    (!cartFullyLoaded || hasLimitExceeded) && e.preventDefault()
                  }
                >
                  {!cartFullyLoaded ? (
                    <div className="flex items-center gap-2">
                      {renderIcon('loader')}
                    </div>
                  ) : (
                    cartConfig.checkout_button_text
                  )}
                </a>
              </div>
              <div>
                <p className="text-center text-xs text-blue-950">
                  {cartConfig.checkout_info_text}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
};

export default CartDrawer;
